import { Dialog, Loading, Notify } from 'quasar';

/**
 * Opens a Quasar Dialog with the given component. When an async component (created via defineAsyncComponent) is
 * provided, it is pre-resolved before creating the dialog to avoid a timing race between Quasar's Dialog plugin
 * show lifecycle and Vue's async component mount cycle.
 *
 * @param {Object} component - The component or async component to render inside the dialog.
 * @param {Object} [componentProps] - Properties to pass to the component.
 * @returns {import('quasar').DialogChainObject} - Chainable dialog API supporting onOk, onCancel, onDismiss.
 */
export function openComponentDialog(component, componentProps = {}) {
    if (!component.__asyncLoader) {
        return Dialog.create({ component, componentProps });
    }
    let dialog = null;
    const pending = [];
    const api = {};
    for (const method of ['onOk', 'onCancel', 'onDismiss']) {
        api[method] = fn => {
            if (dialog) {
                dialog[method](fn);
            } else {
                pending.push([method, fn]);
            }
            return api;
        };
    }
    api.hide = () => { if (dialog) { dialog.hide(); } return api; };
    api.update = props => { if (dialog) { dialog.update(props); } return api; };
    component.__asyncLoader().then(resolved => {
        dialog = Dialog.create({ component: resolved, componentProps });
        pending.forEach(([method, fn]) => dialog[method](fn));
    });
    return api;
}

export function notifySuccess(message, options = {}) {
    return Notify.create({
        type: 'positive',
        progress: true,
        message,
        ...options
    });
}

export function notifyError(message, options = {}) {
    return Notify.create({
        type: 'negative',
        progress: true,
        message,
        ...options
    });
}

export function showLoading() {
    return Loading.show();
}

export function hideLoading() {
    return Loading.hide();
}
